import { msg, plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Sparkles, Undo2 } from 'lucide-react';
import { createContext, use, useState, type ReactNode } from 'react';

import { Alert } from '@/shared/ui/alert';
import { badgeVariants } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

import type { AgentFill } from './agent-fill';

const AgentFillContext = createContext<AgentFill | null>(null);

/** Liga o selo "Luy" dos campos de `shared/form` ao motor do preenchimento da tela (U56). */
export function AgentFillProvider({ fill, children }: { readonly fill: AgentFill | undefined; readonly children: ReactNode }) {
  return <AgentFillContext value={fill ?? null}>{children}</AgentFillContext>;
}

/** O selo do campo, quando o Luy o preencheu e a pessoa ainda não mexeu. */
export function useAgentFillMark(name: string): ReactNode {
  const fill = use(AgentFillContext);
  if (!fill?.isFilled(name)) return null;
  return <AgentFillMark onUndo={() => fill.undo(name)} />;
}

/**
 * Selo "Luy" (discreto, wash de destaque do Lume): abre um balão com o aviso e o "Desfazer" do campo. Mostra 24 px e
 * toca em 44 px (a área invisível do `before`), o toque confortável do iPhone.
 */
function AgentFillMark({ onUndo }: { readonly onUndo: () => void }) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        data-slot="agent-fill-mark"
        aria-label={t`Preenchido pelo Luy`}
        className={cn(badgeVariants({ tone: 'accent' }), 'relative h-6 cursor-pointer px-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ring before:absolute before:-inset-x-1 before:-inset-y-2.5 before:content-[""]')}
      >
        <Sparkles aria-hidden />
        <span aria-hidden>{t`Luy`}</span>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label={t`Preenchido pelo Luy`} className="flex w-64 flex-col gap-2 text-sm">
        <p>{t`O Luy preencheu este campo. Revise antes de salvar.`}</p>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setOpen(false);
            onUndo();
          }}
        >
          <Undo2 aria-hidden />
          {t`Desfazer este campo`}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/** O aviso do topo do formulário: quantos campos o Luy preencheu e o "Desfazer o que o Luy preencheu". */
export function AgentFillNotice({ fill, className }: { readonly fill: AgentFill | undefined; readonly className?: string }) {
  const { t, i18n } = useLingui();
  if (!fill || fill.count === 0) return null;
  const count = fill.count;
  return (
    <Alert
      tone="accent"
      data-slot="agent-fill-notice"
      title={i18n._(msg({ message: plural(count, { one: 'O Luy preencheu # campo', other: 'O Luy preencheu # campos' }) }))}
      {...(className ? { className } : {})}
    >
      {/* O botão vai embaixo do texto: ao lado, no iPhone, ele espremia o aviso até sumir. */}
      <div className="flex flex-col items-start gap-2">
        <p>{t`Revise os campos com o selo Luy. Salvar e enviar continuam com você.`}</p>
        <Button size="sm" variant="secondary" onClick={() => fill.undo()}>
          <Undo2 aria-hidden />
          {t`Desfazer o que o Luy preencheu`}
        </Button>
      </div>
    </Alert>
  );
}
